<!--
  AI:
  Status: "in progress"
  Type: Component
  SubType: ObjectComponent
  Reason: ObjectBaseComponent template for SmSigningProcess with eight navigation items (Details,
    Verlauf, Unterzeichner, Benachrichtigungen, Dokumente, Platzierung, Vor Ort unterschreiben, Audit
    Trail), child entity widgets and the two read-only surfaces of A-1/A-2.
-->
@if (initialized && dto) {
    <div class="sm-signing-process-container">

        <!-- Process Header -->
        <div class="flex justify-content-between align-items-center p-3 mb-3 surface-card border-round shadow-1">
            <div class="flex align-items-center gap-3">
                <i class="fa-regular fa-file-signature text-2xl text-primary"></i>
                <div>
                    <h4 class="m-0 font-semibold">{{ dto.name }}</h4>
                    <span class="text-sm text-color-secondary">Prozess #{{ dto.id }}</span>
                </div>
                <p-tag [value]="getStatusLabel(dto.processStatus)"
                       [severity]="getStatusSeverity(dto.processStatus)">
                </p-tag>
            </div>
            <div class="flex align-items-center gap-2">
                @if (dto.processStatus === 0 || dto.processStatus === 1) {
                    <p-button label="Abbrechen"
                              icon="fa-regular fa-ban"
                              severity="danger"
                              [outlined]="true"
                              [loading]="actionBusy"
                              (onClick)="cancelProcess()">
                    </p-button>
                }
            </div>
        </div>

        <!-- Tab Content -->
        @if (activeNavigationItems?.action === 'details') {
            <div class="p-3">
                @if (detailWidget) {
                    <!-- AI: Widget for SmSigningProcess entity data display -->
                    <mvs-widget [widgetData]="detailWidget"></mvs-widget>
                }

                <!-- Additional detail info cards -->
                <div class="grid mt-3">
                    <div class="col-12 md:col-6">
                        <div class="surface-card p-3 border-round shadow-1">
                            <h5 class="font-semibold mb-3">
                                <i class="fa-regular fa-gear mr-2"></i>Konfiguration
                            </h5>
                            <div class="flex flex-column gap-2">
                                <div class="flex justify-content-between">
                                    <span class="text-color-secondary">Signaturordnung</span>
                                    <span class="font-medium">{{ getSigningOrderLabel(dto.signingOrder) }}</span>
                                </div>
                                @if (dto.certificateDtoName) {
                                    <div class="flex justify-content-between">
                                        <span class="text-color-secondary">Zertifikat</span>
                                        <span class="font-medium">{{ dto.certificateDtoName }}</span>
                                    </div>
                                }
                                @if (dto.channelTypeDtoName) {
                                    <div class="flex justify-content-between">
                                        <span class="text-color-secondary">Kanal</span>
                                        <span class="font-medium">{{ dto.channelTypeDtoName }}</span>
                                    </div>
                                }
                                @if (dto.contactType) {
                                    <div class="flex justify-content-between">
                                        <span class="text-color-secondary">Kontakttyp</span>
                                        <span class="font-medium">{{ dto.contactType }}</span>
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                    <div class="col-12 md:col-6">
                        <div class="surface-card p-3 border-round shadow-1">
                            <h5 class="font-semibold mb-3">
                                <i class="fa-regular fa-clock mr-2"></i>Zeitangaben
                            </h5>
                            <div class="flex flex-column gap-2">
                                <!--
                                  dd.MM.yyyy HH:mm — the repo's own date convention (roughly a
                                  hundred sites) and the format the UX standard demands. Both fields
                                  arrive as raw ISO-8601 instants and used to be printed verbatim,
                                  which put „2026-09-08T11:42:07.512Z“ on a caseworker's screen.

                                  The unused mvsDateFormat pipe is deliberately NOT revived for this:
                                  a second date path next to the one every other surface uses is a
                                  second thing to keep in step.
                                -->
                                @if (dto.expiresAt) {
                                    <div class="flex justify-content-between">
                                        <span class="text-color-secondary">Ablaufdatum</span>
                                        <span class="font-medium">{{ dto.expiresAt | date:'dd.MM.yyyy HH:mm' }}</span>
                                    </div>
                                }
                                @if (dto.completedAt) {
                                    <div class="flex justify-content-between">
                                        <span class="text-color-secondary">Abgeschlossen am</span>
                                        <span class="font-medium">{{ dto.completedAt | date:'dd.MM.yyyy HH:mm' }}</span>
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                </div>

                @if (dto.objectType) {
                    <div class="surface-card p-3 border-round shadow-1 mt-3">
                        <h5 class="font-semibold mb-3">
                            <i class="fa-regular fa-link mr-2"></i>Verknüpftes Objekt
                        </h5>
                        <div class="flex align-items-center gap-3">
                            <span class="text-color-secondary">{{ dto.objectType }}</span>
                            <span class="font-medium">#{{ dto.objectId }}</span>
                        </div>
                    </div>
                }
            </div>
        }

        <!--
          Verlauf (A-2). Its own component, not mvs-widget-timeline: that widget extends
          MvsWidgetListComponent and is row-HOMOGENEOUS — one entity, one dataProviderObject for
          every row, one fixed marker icon — and this track carries five entity types, a per-signer
          lane and a per-row marker taken from the row's own backend metadata. Signed as
          JUSTIFIED-CUSTOM (FD-4).

          Read-only. Versand, „Link neu ausstellen“ und Abbruch stay where they are, and the raw
          hash chain stays in „Audit Trail“ — the Verlauf does not replace that tab.
        -->
        @if (activeNavigationItems?.action === 'timeline') {
            <mvs-sm-signing-process-timeline [signingProcessId]="dto.id">
            </mvs-sm-signing-process-timeline>
        }

        @if (activeNavigationItems?.action === 'signers') {
            <div class="p-3">

                <!--
                  The way to add a signer. The table's own create action is gone: it opened the
                  platform's metadata form against sm.SmSigningProcessSigner, whose registered route
                  has no controller in the backend, so the dialog appeared with a header and not one
                  field and nothing could be entered. This button opens a dialog against the endpoint
                  that exists — and the one that also captures the person anchor the delivery address
                  belongs to.
                -->
                <div class="flex justify-content-end mb-3">
                    <p-button label="Unterzeichner hinzufügen"
                              icon="fa-regular fa-user-plus"
                              size="small"
                              [loading]="signerDialogOpening"
                              [disabled]="actionBusy || dispatching"
                              (onClick)="openSignerDialog()">
                    </p-button>
                </div>

                @if (signerWidget) {
                    <!-- AI: Widget for SmSigningProcessSigner child entity table -->
                    <mvs-widget [widgetData]="signerWidget"
                                (onChangedObject)="handleChildChanged($event)">
                    </mvs-widget>
                }

                <!--
                  The FIRST dispatch, which the link panel below deliberately does not offer. Its
                  "Link neu ausstellen" is the way BACK: it revokes the credential the customer is
                  holding, clears the one-time password lock and moves the deadline. Used as a first
                  dispatch it destroys nothing only because there was nothing to destroy yet, and used
                  on a running process it silently invalidates a link somebody may be signing on.
                -->
                <div class="surface-card border-round shadow-1 p-3 mt-4">

                    <div class="flex justify-content-between align-items-center gap-3 flex-wrap">
                        <div class="flex align-items-center gap-2">
                            <i class="fa-regular fa-paper-plane"></i>
                            <span class="font-semibold">Versand</span>
                        </div>
                        @if (canDispatch) {
                            <p-button label="Versand starten"
                                      icon="fa-regular fa-paper-plane"
                                      size="small"
                                      [loading]="dispatching"
                                      [disabled]="actionBusy"
                                      (onClick)="dispatchProcess()">
                            </p-button>
                        }
                    </div>

                    @if (canDispatch) {
                        <span class="block text-sm text-color-secondary mt-2">
                            Stellt die Einladungen aus und stellt sie über die hinterlegten
                            Kanalvarianten zu. Bei sequenzieller Signaturordnung wird nur die Person
                            mit der niedrigsten Reihenfolge eingeladen; die übrigen folgen, sobald
                            diese unterschrieben hat. Ein erneuter Aufruf stellt bereits zugestellte
                            Einladungen nicht noch einmal zu.
                        </span>
                    } @else {
                        <span class="block text-sm text-color-secondary mt-2">
                            Für einen abgeschlossenen oder abgebrochenen Signaturprozess kann kein
                            Versand mehr gestartet werden.
                        </span>
                    }

                    <!-- The server's own sentence: es sagt, welche Regel abgelehnt hat. -->
                    @if (dispatchError) {
                        <p-message severity="error"
                                   [text]="dispatchError"
                                   styleClass="w-full mt-3">
                        </p-message>
                    }

                    <!--
                      Every signer that was considered, each with its own outcome. Deliberately not a
                      single verdict: each dispatch runs in a transaction of its own, so one refusal
                      leaves the others delivered, and a surface that showed only the count would hide
                      exactly the half a caseworker has to act on.
                    -->
                    @if (dispatchResult) {
                        <div class="mt-3 flex flex-column gap-2">
                            @for (result of dispatchResult.signers; track result.signerId) {
                                <div class="surface-100 border-round p-3">
                                    <div class="flex justify-content-between align-items-center gap-3 flex-wrap">
                                        <span class="font-medium">
                                            {{ result.signerName || 'Unterzeichner #' + result.signerId }}
                                        </span>
                                        <p-tag [value]="dispatchOutcomeLabel(result)"
                                               [severity]="dispatchOutcomeSeverity(result)">
                                        </p-tag>
                                    </div>
                                    @if (!dispatchSucceeded(result) && result.refusalReason) {
                                        <p-message severity="warn"
                                                   [text]="result.refusalReason"
                                                   styleClass="w-full mt-2">
                                        </p-message>
                                    }
                                </div>
                            }
                            @if (dispatchResult.signers.length === 0) {
                                <span class="text-sm text-color-secondary">
                                    Es wurde kein Unterzeichner für den Versand berücksichtigt.
                                </span>
                            }
                        </div>
                    }
                </div>

                <!--
                  The reissue of the external signing link, per signer. It sits under the signer
                  table and not inside it: one click revokes the old credential, mints and delivers
                  a new one, clears the second-factor lock and moves the deadline, and a consequence
                  of that weight needs its own confirmation rather than a row action.

                  This is also the ONLY way back. A link that expired or an OTP that locked out has
                  no self-service reset for the customer — the external application tells them to
                  contact their caseworker, and this is what the caseworker does in answer.
                -->
                <div class="mt-4">
                    <mvs-sm-signer-link-panel [processId]="dto.id"
                                          (onLinkReissued)="handleLinkReissued()">
                    </mvs-sm-signer-link-panel>
                </div>
            </div>
        }

        <!--
          Benachrichtigungen (A-1): what was sent to the UNTERZEICHNERN, and what became of it.
        -->
        @if (activeNavigationItems?.action === 'notifications') {
            <div class="p-3">

                <!--
                  BINDING. Refused dispatches are reported to the SACHBEARBEITUNG, so their
                  recipient row is anchored on a um.User and no filter over this table can reach
                  them; they stand in the Verlauf as audit events instead. A tab that quietly left
                  them out would be worse than no tab, because a caseworker who does not find a
                  refusal here would conclude none happened.
                -->
                <p-message severity="info"
                           [text]="notificationsHint"
                           styleClass="w-full mb-3">
                </p-message>

                <!-- LOADING: the signer ids are being resolved; the table cannot exist before them. -->
                @if (notificationsState === 'loading' || notificationsState === 'idle') {
                    <div class="flex flex-column gap-2">
                        <p-skeleton width="100%" height="2rem" borderRadius="0.5rem"></p-skeleton>
                        <p-skeleton width="100%" height="10rem" borderRadius="0.5rem"></p-skeleton>
                    </div>
                }

                <!-- ERROR -->
                @if (notificationsState === 'error') {
                    <div class="flex flex-column align-items-start gap-3">
                        <p-message severity="error" [text]="notificationsError" styleClass="w-full"></p-message>
                        <p-button label="Erneut laden"
                                  icon="fa-regular fa-arrows-rotate"
                                  severity="secondary"
                                  size="small"
                                  (onClick)="retryNotificationsLoad()">
                        </p-button>
                    </div>
                }

                <!--
                  READY. The EMPTY state is the table's own noDataText, set to the German sentence
                  the QL logic file exports — a process with no signers and a process whose signers
                  were never written to both land there through one code path.
                -->
                @if (notificationsState === 'ready' && notificationsWidget) {
                    <mvs-widget [widgetData]="notificationsWidget">

                        <!--
                          „Unterzeichner“. objectId is a bare Long with no relationship, so no QL
                          join reaches the name and no caller-supplied value list reaches a cell
                          (widget.service.ts:331-366 copies nine properties and valueList is not one
                          of them). This is the widget custom-field seam, which receives the whole
                          row; the name comes from the same getProcess read that produced the ids
                          the query filters on, so the column costs no request. See
                          notificationSignerLabel() for why it does NOT host mvs-object-inline.
                        -->
                        <ng-template let-object="object" mvs-widget-custom-field-directive
                                     area="columns" fieldId="signer" label="Unterzeichner">
                            <span class="font-medium">{{ notificationSignerLabel(object) }}</span>
                        </ng-template>

                        <!--
                          Das gerenderte Schreiben — angezeigt, nicht heruntergeladen (FD-6). A plain
                          cell could print the document's name and nothing else; the document id is
                          projected but never shown, because a raw dm.DmDocument key says nothing to
                          a caseworker.
                        -->
                        <ng-template let-object="object" mvs-widget-custom-field-directive
                                     area="columns" fieldId="renderedMail" label="Gerendertes Schreiben">
                            @if (notificationMailDocumentId(object)) {
                                <p-button [label]="notificationMailLabel(object)"
                                          icon="fa-regular fa-envelope-open-text"
                                          severity="secondary"
                                          size="small"
                                          [text]="true"
                                          (onClick)="openRenderedMail(object)">
                                </p-button>
                            } @else {
                                <span class="text-color-secondary">—</span>
                            }
                        </ng-template>
                    </mvs-widget>
                }
            </div>
        }

        @if (activeNavigationItems?.action === 'documents') {
            <div class="p-3">
                @if (documentVersionWidget) {
                    <!-- AI: Widget for SmDocumentVersion child entity table -->
                    <mvs-widget [widgetData]="documentVersionWidget"></mvs-widget>
                }
            </div>
        }

        <!--
          Free placement (derivation mode manualPlacement). Loaded lazily: /layout costs a full PDF
          parse and the source document is a second download, and most visits never open this tab.
        -->
        @if (activeNavigationItems?.action === 'placement') {
            <div class="p-3">

                @if (placementState === 'loading') {
                    <div class="flex flex-column align-items-center gap-2 p-5">
                        <p-progressSpinner styleClass="w-3rem h-3rem"></p-progressSpinner>
                        <span class="text-sm">Die Platzierungsdaten werden geladen …</span>
                    </div>
                }

                @if (placementState === 'error') {
                    <div class="flex flex-column align-items-start gap-3">
                        <p-message severity="error" [text]="placementError" styleClass="w-full"></p-message>
                        <p-button label="Erneut laden"
                                  icon="fa-regular fa-arrows-rotate"
                                  severity="secondary"
                                  size="small"
                                  (onClick)="retryPlacementLoad()"></p-button>
                    </div>
                }

                @if (placementState === 'ready') {

                    @if (placementRoles.length === 0) {
                        <p-message severity="warn"
                                   text="Dieser Signaturprozess hat noch keine Unterzeichner. Ohne Unterzeichner kann kein Rechteck einer Rolle zugeordnet werden — bitte legen Sie die Unterzeichner zuerst im Reiter „Unterzeichner“ an."
                                   styleClass="w-full mb-3">
                        </p-message>
                    }

                    @if (placementDirty) {
                        <p-message severity="info"
                                   text="Es liegen noch nicht gespeicherte Änderungen an der Platzierung vor."
                                   styleClass="w-full mb-3">
                        </p-message>
                    }

                    <!-- AI: The free placement tool — draws the document, takes the rectangles. -->
                    <mvs-sm-placement-tool [document]="placementDocument"
                                       [pages]="placementPages"
                                       [roles]="placementRoles"
                                       [placements]="placements"
                                       [saving]="placementSaving"
                                       (placementsChanged)="onPlacementsChanged($event)"
                                       (save)="onPlacementsSave($event)">
                    </mvs-sm-placement-tool>
                }
            </div>
        }

        <!--
          Signing on the spot, for a REAL process. The guided flow used to hang only off the
          playground's demo process, so the roles of an actual document could be signed over REST and
          in no other way. It is the same component, driven by this process's id; who is next in line,
          which places are still open and whether an in-person identification is demanded first are
          all the flow's own business.
        -->
        @if (activeNavigationItems?.action === 'signInPerson') {
            <div class="p-3">
                @if (dto.processStatus === 2 || dto.processStatus === 3) {
                    <p-message severity="info"
                               text="Dieser Signaturprozess ist abgeschlossen oder abgebrochen. Es können keine weiteren Unterschriften geleistet werden."
                               styleClass="w-full">
                    </p-message>
                } @else {
                    <!-- AI: The guided signing flow for the open roles of this process. -->
                    <mvs-sm-signing-flow [processId]="dto.id"
                                         (onSignatureSubmitted)="handleInPersonSignature()"
                                         (onProcessCompleted)="handleInPersonSignature()">
                    </mvs-sm-signing-flow>
                }
            </div>
        }

        @if (activeNavigationItems?.action === 'auditTrail') {
            <div class="p-3">
                @if (auditEventWidget) {
                    <!-- AI: Widget for SmAuditEvent child entity table -->
                    <mvs-widget [widgetData]="auditEventWidget"></mvs-widget>
                }
            </div>
        }

        <!--
          Outside the tab conditions on purpose. A dialog declared inside the signers tab would be
          torn out of the DOM the moment the tab changed, and the change that follows a successful
          create is exactly such a moment.
        -->
        <mvs-sm-signer-create-dialog
            [processId]="dto.id"
            [signatureProfileId]="signerDialogProfileId"
            [(visible)]="signerDialogVisible"
            (onSignerCreated)="handleSignerCreated()">
        </mvs-sm-signer-create-dialog>
    </div>
}
